// general-translation.tsx
import React, { useState, useEffect } from "react";
import { Form, Row, Col, Typography, Divider, Spin, Skeleton } from "antd";
import type { FormInstance } from "antd/es/form";
import { ISettingsField } from "src/Interfaces/generalSettings";
import { Select, Checkbox, Input } from "antd";
import { getSettingsPage } from "src/Api/content";
import { useLocale } from "src/i18n/localeContext";
import { StrapiError } from "src/pages/StrapiError";

// const { Title } = Typography;
import { Flex, Heading, Button } from "@nubeteck/components";
import ConfigurationInput from "./generalSettings/settingsInput";
import { SHORT_TEXT, EDITOR, useSuccessRegisterToast } from "src/constants";

export interface ISettingsFieldExtended extends Omit<ISettingsField, "id" | "descripcion"> {
  id?: number;
  descripcion?: string;
  nombre: string;
  label: string;
  clave?: string;
  tipo: string;
  valor?: any;
  value?: any;
  requerido?: boolean;
  opciones?: Array<{ label: string; value: any }>;
  suppressTranslation?: boolean;
  [k: string]: any;
}

interface GeneralTranslationProps {
  fields: ISettingsFieldExtended[];
  title?: string;
  onSave?: (payload: { es: Record<string, any>; en: Record<string, any> }) => Promise<void> | void;
  loading?: boolean;
  onBack?: () => void;
  showSaveButton?: boolean;
  readOnlyLeft?: boolean;
}

/** Estilo panel (columna) */
const panelStyle: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  padding: 12,
  height: "calc(100vh - 210px)",
  overflow: "auto",
  background: "#fff",
};

function useCustomScrollbarOnce() {
  React.useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .custom-scroll::-webkit-scrollbar { width: 6px; }
      .custom-scroll::-webkit-scrollbar-thumb { background-color: #bdbdbd; border-radius: 4px; }
      .custom-scroll::-webkit-scrollbar-thumb:hover { background-color: #9e9e9e; }
      .custom-scroll::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
      .custom-scroll { scrollbar-width: thin; scrollbar-color: #bdbdbd #f1f1f1; }
    `;
    document.head.appendChild(style);
    return () => {
      try {
        document.head.removeChild(style);
      } catch {
        /* noop */
      }
    };
  }, []);
}

function getVisibleLabel(f: ISettingsFieldExtended) {
  return f.label ?? f.clave ?? "";
}

function projectFieldForConfigInput(base: ISettingsFieldExtended): ISettingsField {
  return {
    id: base.id ?? 0,
    nombre: base.nombre,
    label: getVisibleLabel(base),
    descripcion: base.descripcion ?? "",
    tipo: base.tipo,
    valor: base.valor,
    value: base.value,
    requerido: base.requerido,
    clave: base.clave,
    useVariables: base.useVariables,
    variables: base.variables,
    opciones: base.opciones,
    language: base.language,
  };
}

function initForms(fields: ISettingsFieldExtended[], formES: FormInstance, formEN: FormInstance) {
  const initES: Record<string, any> = {};
  const initEN: Record<string, any> = {};

  fields.forEach((f) => {
    initES[f.nombre] = typeof f.valor === "undefined" ? (f.tipo === "CHECKBOX" ? false : "") : f.valor;

    if (!f.suppressTranslation) {
      initEN[`${f.nombre}_en`] = typeof f.value === "undefined" || f.value === null || f.value === ""
        ? (f.tipo === "CHECKBOX" ? false : "")
        : f.value;
    } else {
      initEN[`${f.nombre}_en`] = initES[f.nombre];
    }
  });

  formES.setFieldsValue(initES);
  formEN.setFieldsValue(initEN);
}

/** Renderizador controlado por tipo (ES/EN) */
function renderByType(
  base: ISettingsFieldExtended,
  lang: "es" | "en",
  form: FormInstance,
  disabled: boolean,
) {
  const name = lang === "es" ? base.nombre : `${base.nombre}_en`;

  if (base.tipo === SHORT_TEXT || base.tipo === EDITOR) {
    const formValue = form.getFieldValue(name);
    const baseValue = lang === "es" 
      ? (base.valor !== undefined && base.valor !== null ? base.valor : "") 
      : (base.value !== undefined && base.value !== null ? base.value : "");
    const dataValue = (formValue !== undefined && formValue !== null) 
      ? formValue 
      : (baseValue !== undefined && baseValue !== null ? baseValue : "");
    
    const f = projectFieldForConfigInput(base);
    return (
      <ConfigurationInput
        key={`${name}`}
        data={{
          ...f,
          valor: lang === "es" ? dataValue : undefined,
          value: lang === "en" ? dataValue : undefined,
        }}
        onChange={(val) => {
          form.setFieldValue(name, val);
        }}
        disabled={disabled}
      />
    );
  }

  if (base.tipo === "SELECT") {
    return (
      <Select
        options={base.opciones ?? []}
        value={form.getFieldValue(name)}
        onChange={(v) => form.setFieldValue(name, v)}
        disabled={disabled}
        allowClear={!disabled}
        size="large"
        placeholder="Tipo"
      />
    );
  }

  if (base.tipo === "CHECKBOX") {
    return (
      <Checkbox
        checked={!!form.getFieldValue(name)}
        onChange={(e) => form.setFieldValue(name, e.target.checked)}
        disabled={disabled}
      >
      </Checkbox>
    );
  }

  return (
    <Input
      type="text"
      value={form.getFieldValue(name)}
      onChange={(e) => form.setFieldValue(name, e.target.value)}
      disabled={disabled}
    />
  );
}

const GeneralTranslation: React.FC<GeneralTranslationProps> = ({
  fields,
  title = "Traducción de plantilla",
  onSave,
  loading = false,
  onBack,
  showSaveButton = true,
  readOnlyLeft = false,
}) => {
  useCustomScrollbarOnce();
  const { locale } = useLocale();
  const [settingsPage, setSettingsPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState(false);
  const showSuccessToast = useSuccessRegisterToast();

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setSettingsPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getSettingsPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setSettingsPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const [formES] = Form.useForm();
  const [formEN] = Form.useForm();

  React.useEffect(() => {
    // Solo inicializar formularios cuando settingsPage esté disponible para evitar valores por defecto
    if (!isLoadingContent && settingsPage && fields && fields.length) {
      initForms(fields, formES, formEN);
    } else if (!isLoadingContent && settingsPage) {
      formES.resetFields();
      formEN.resetFields();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, isLoadingContent, settingsPage]);

  const handleSave = async () => {
    try {
      const es = await formES.validateFields();
      const enFieldNames = fields
        .filter((f) => !f.suppressTranslation)
        .map((f) => `${f.nombre}_en`);
      await formEN.validateFields(enFieldNames);

      const enRaw = formEN.getFieldsValue(enFieldNames);
      const en: Record<string, any> = {};
      Object.keys(enRaw).forEach((k) => {
        if (k.endsWith("_en")) en[k.replace(/_en$/, "")] = enRaw[k];
      });

      await onSave?.({ es, en });
      showSuccessToast();
    } catch {
      /* antd resaltará errores */
    }
  };

  if (loading || isLoadingContent) {
    return (
      <Flex style={{ width: "100%", flexDirection: "column", gap: 16 }}>
        <Skeleton.Input active size="large" style={{ width: 300 }} />
        <Skeleton active paragraph={{ rows: 12 }} />
      </Flex>
    );
  }
    if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setSettingsPage(null);
      }}
    />;
  }

  // Si settingsPage no está disponible, mostrar skeleton
  if (!settingsPage) {
    return (
      <Flex style={{ width: "100%", flexDirection: "column", gap: 16, maxWidth: 1440 }}>
        <Skeleton.Input active size="large" style={{ width: 300 }} />
        <Skeleton active paragraph={{ rows: 12 }} />
      </Flex>
    );
  }

  return (
    <Flex style={{ width: "100%", flexDirection: "column", gap: 16, maxWidth: 1440 }}>
      {/* Barra superior */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Heading level={4}>{settingsPage?.translate_template.template_translate || title}</Heading>
      </div>

      {/* Dos columnas: ES | EN */}
      <Row gutter={16} style={{ width: "100%" }}>
        {/* Columna ES */}
        <Col xs={24} md={12}>
          <div className="custom-scroll" style={panelStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography.Title level={5} style={{ margin: 0 }}>Español</Typography.Title>
            </div>
            <Divider style={{ margin: "12px 0" }} />
            <Spin spinning={false}>
              <Form form={formES} layout="vertical" disabled={readOnlyLeft}>
                {fields.map((base) => (
                  <Form.Item
                    key={`es-${base.nombre}`}
                    name={base.nombre}
                    label={getVisibleLabel(base)}
                    valuePropName={base.tipo === "CHECKBOX" ? "checked" : "value"}
                    hasFeedback
                    required={base.requerido === true}
                    rules={[
                      {
                        required: base.requerido === true,
                        message: "Este campo es requerido.",
                      },
                    ]}
                  >
                    <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues[base.nombre] !== currentValues[base.nombre]}>
                      {() => renderByType(base, "es", formES, !!readOnlyLeft)}
                    </Form.Item>
                  </Form.Item>
                ))}
              </Form>
            </Spin>
          </div>
        </Col>

        {/* Columna EN */}
        <Col xs={24} md={12}>
          <div className="custom-scroll" style={panelStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography.Title level={5} style={{ margin: 0 }}>English</Typography.Title>
            </div>
            <Divider style={{ margin: "12px 0" }} />
            <Spin spinning={false}>
              <Form form={formEN} layout="vertical">
                {fields.map((base) => {
                  const nameEN = `${base.nombre}_en`;
                  const isTraducible = !base.suppressTranslation;
                  return (
                    <Form.Item
                      key={`en-${nameEN}`}
                      name={isTraducible ? nameEN : nameEN}
                      label={getVisibleLabel(base)}
                      valuePropName={
                        base.tipo === "CHECKBOX" ? "checked" : "value"
                      }
                      hasFeedback
                      rules={[{ required: false }]}
                    >
                      <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues[nameEN] !== currentValues[nameEN]}>
                        {() => (
                          isTraducible
                            ? renderByType(base, "en", formEN, false)
                            : renderByType(base, "en", formEN, true)
                        )}
                      </Form.Item>
                    </Form.Item>
                  );
                })}
              </Form>
            </Spin>
          </div>
        </Col>
      </Row>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {onBack && <Button onClick={onBack}>{settingsPage?.translate_template.back_button || "Retroceder"}</Button>}
          {showSaveButton && (
            <Button type="primary" onClick={handleSave}>
            {settingsPage?.translate_template.save_button || "Guardar"}
          </Button>
        )}
      </div>
    </Flex>
  );
};

export default GeneralTranslation;